import React from 'react';
import {
  View,
  Flex,
  Text,
  ActionButton,
  Heading,
  Image
} from '@adobe/react-spectrum';
import { useDispatch, useSelector } from 'react-redux';
import { selectLegend } from 'app/pages/Home/slice/selectors';
import { useHomeSlice } from 'app/pages/Home/slice';
import Close from '@spectrum-icons/workflow/Close';
import CheckMarkCircleIcon from 'app/assets/svgIcons/CheckMarkCircleIcon.js';
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import { LegendReleaseLevelIcon } from 'app/assets/legend/LegendReleaseLevelIcon';
import { LegendFeatureReleaseIcon } from 'app/assets/legend/LegendFeatureReleaseIcon';
import { LegendBetaIcon } from 'app/assets/legend/LegendBetaIcon';
import { LegendPrivateBetaIcon } from 'app/assets/legend/LegendPrivateBetaIcon';
import { LegendCompletedIcon } from 'app/assets/legend/LegendCompletedIcon';
import { LegendAlertTriangleIcon } from 'app/assets/legend/LegendAlertTriangleIcon';
import { LegendLinkedEventsIcon } from 'app/assets/legend/LegendLinkedEventsIcon';
import { LegendNoMarketingIcon } from 'app/assets/legend/LegendNoMarketingIcon';
import { RevenueDriverIcon } from 'app/assets/legend/RevenueDriverIcon';
import {
  LegendEolStageBadge,
  type LegendEolStageAbbrev
} from 'app/assets/legend/LegendEolStageBadge';
import styles from './Legend.module.scss';
import Classnames from 'classnames';
import { NoMarketingIcon } from 'app/assets/legend/NoMarketingIcon';

const EOL_LEGEND_ITEMS: ReadonlyArray<{
  abbrev: LegendEolStageAbbrev;
  stageLabel: string;
  testId: string;
}> = [
  {
    abbrev: 'EOL',
    stageLabel: 'Initial Announcement',
    testId: 'legend-item-eol-initial-announcement'
  },
  {
    abbrev: 'EOSa',
    stageLabel: 'End Of Sale',
    testId: 'legend-item-eol-end-of-sale'
  },
  {
    abbrev: 'EOSu',
    stageLabel: 'End Of Support',
    testId: 'legend-item-eol-end-of-support'
  },
  {
    abbrev: 'Decom',
    stageLabel: 'Decommission',
    testId: 'legend-item-eol-decommission'
  }
];

export const Legend = () => {
  const showLegend = useSelector(selectLegend);
  const { actions } = useHomeSlice();
  const dispatch = useDispatch();

  return (
    <View
      UNSAFE_className={Classnames([
        styles.legendContainer,
        showLegend ? styles.show : styles.hide
      ])}
      data-testid="legend-container"
    >
      <Flex UNSAFE_className={styles.legendHeader} data-testid="legend-header">
        <ActionButton
          onPress={() => {
            dispatch(actions.setLegend(false));
          }}
          data-testid="legend-close-button"
        >
          <Close />
        </ActionButton>
        <span className={styles.separator}></span>
        <Heading
          level={2}
          marginY="0"
          UNSAFE_className={styles.legendHeading}
          data-testid="legend-heading"
        >
          Legend
        </Heading>
      </Flex>
      <Flex
        UNSAFE_className={styles.legendContent}
        data-testid="legend-content"
      >
        <Flex
          direction={'row'}
          gap="size-200"
          data-testid="legend-item-risk-low"
        >
          <LegendRiskLowIcon width={24} height={24} />
          <Text>
            Risk Level: <Text UNSAFE_className={styles.boldText}>Low</Text>
          </Text>
        </Flex>
        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-risk-moderate"
        >
          <LegendRiskModerateIcon width={24} height={24} />
          <Text>
            Risk Level: <Text UNSAFE_className={styles.boldText}>Moderate</Text>
          </Text>
        </Flex>
        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-risk-high"
        >
          <LegendRiskHighIcon width={24} height={24} />
          <Text>
            Risk Level: <Text UNSAFE_className={styles.boldText}>High</Text>
          </Text>
        </Flex>
        <Flex
          direction={'row'}
          gap={'size-250'}
          data-testid="legend-item-in-planning"
        >
          <View marginStart={'size-50'}>
            <LegendInPlanningIcon width={16} height={16} />
          </View>
          <Text>In Planning</Text>
        </Flex>
        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-release-level"
        >
          <LegendReleaseLevelIcon width={24} height={24} />
          <Text>
            Release Level{' '}
            <Text UNSAFE_className={styles.boldText}>(L1, L2, L3)</Text>
          </Text>
        </Flex>
        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-feature-release"
        >
          <LegendFeatureReleaseIcon width={24} height={24} />
          <Text>Product/Feature Release</Text>
        </Flex>

        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-public-beta"
        >
          <LegendBetaIcon width={24} height={24} />
          <Text>Public Beta</Text>
        </Flex>
        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-private-beta"
        >
          <LegendPrivateBetaIcon width={24} height={24} />
          <Text>Private Beta</Text>
        </Flex>
        {/* <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-completed"
        >
          <LegendCompletedIcon width={24} height={24} />
          <Text>Completed</Text>
        </Flex> */}
        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-requires-attention"
        >
          <LegendAlertTriangleIcon width={24} height={24} />
          <Text>Requires Attention</Text>
        </Flex>

        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-linked-events"
        >
          <LegendLinkedEventsIcon width={24} height={24} />
          <Text>Linked Events</Text>
        </Flex>
        <Flex
          direction={'row'}
          gap={'size-200'}
          data-testid="legend-item-no-marketing"
        >
          <LegendNoMarketingIcon width={24} height={24} />
          <Text>Release, No Marketing</Text>
        </Flex>
        <Flex
          direction={'row'}
          gap={'size-200'}
          alignItems="center"
          data-testid="legend-item-revenue-driver"
        >
          <View
            UNSAFE_style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px'
            }}
          >
            <RevenueDriverIcon width={18} height={18} />
          </View>
          <Text>Impacts Revenue Targets</Text>
        </Flex>
        {EOL_LEGEND_ITEMS.map(({ abbrev, stageLabel, testId }) => (
          <Flex
            key={testId}
            direction={'row'}
            gap={'size-200'}
            UNSAFE_className={styles.legendEolRow}
            data-testid={testId}
          >
            <LegendEolStageBadge
              abbrev={abbrev}
              className={styles.legendEolIconSlot}
              badgeClassName={styles.legendEolBadge}
            />
            <Text>
              End of Life:{' '}
              <Text UNSAFE_className={styles.boldText}>{stageLabel}</Text>
            </Text>
          </Flex>
        ))}
      </Flex>
    </View>
  );
};
